import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import {
  buildLoginUrlWithRedirect,
  buildSignupUrlWithRedirect,
  type AuthIntent,
} from '@/lib/postLoginRedirect'

import { goldGradientButtonSm } from '@/lib/brandGradient'
import { glassSurface } from '@/lib/glassSurface'
import { cn } from '@/lib/cn'
import logoUrl from '@/assets/vaagon-logo.svg'

const BrandLogoIcon = ({ className }: { className?: string }) => (
  <img
    src={logoUrl}
    alt="Vaagon"
    className={cn('h-10 w-auto object-contain', className)}
  />
)

export const AuthPromptModal = ({
  open,
  onClose,
  title = 'Sign in to vote',
  description = 'Log in or create an account to upvote or downvote ideas and join the validation.',
  returnTo,
  intent,
}: {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  /** Where to land after signing in. Defaults to the page showing the prompt. */
  returnTo?: string
  /** The action that was blocked, so the page can open the right UI on arrival. */
  intent?: AuthIntent | null
}) => {
  const navigate = useNavigate()
  const location = useLocation()

  // Default to the current URL so a visitor always comes back to what they
  // were looking at. Callers pass `returnTo` when the action should land them
  // somewhere else (e.g. "Submit your idea" going to /submit-idea).
  const target = returnTo ?? `${location.pathname}${location.search}`
  const [targetPath, targetSearch = ''] = target.split('?')
  const search = targetSearch ? `?${targetSearch}` : ''

  useBodyScrollLock(open)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const modal = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden bg-[#050505]/60 px-4 py-6 backdrop-blur-[20px]">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-prompt-title"
        className={cn(
          'relative my-auto box-border flex w-full max-w-[min(380px,calc(100vw-2rem))] shrink-0 flex-col rounded-2xl px-6 pt-12 pb-8 text-white shadow-2xl',
          glassSurface,
        )}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 grid h-7 w-7 place-items-center rounded-full border border-white/10 bg-white/5 text-slate-200 transition hover:bg-white/10 hover:text-white"
        >
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            className="h-3 w-3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <path d="M6 6 L18 18 M18 6 L6 18" />
          </svg>
        </button>

        <div className="flex flex-col items-center text-center">
          <BrandLogoIcon className="mb-4" />
          <h2
            id="auth-prompt-title"
            className="text-xl font-semibold text-white"
          >
            {title}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-300/70">
            {description}
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <button
            type="button"
            onClick={() =>
              navigate(
                buildLoginUrlWithRedirect(targetPath, search, '', intent),
              )
            }
            className={cn(
              'flex h-11 w-full items-center justify-center text-sm font-semibold',
              goldGradientButtonSm,
            )}
          >
            Log in
          </button>
          <button
            type="button"
            onClick={() =>
              navigate(
                buildSignupUrlWithRedirect(targetPath, search, '', intent),
              )
            }
            className="flex h-11 w-full items-center justify-center rounded-full border border-[color:var(--btn-outline-border)] bg-transparent text-sm font-medium text-white transition hover:border-[color:var(--btn-outline-border-hover)] hover:bg-transparent"
          >
            Sign up
          </button>
        </div>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return modal
  return createPortal(modal, document.body)
}
